import {
  Button,
  Stack,
  Grid,
  OutlinedInput,
  Autocomplete,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import Modal, { CommonModalProps } from "..";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { DbAccessDealInfos } from "../../../Core/Api/Types/db-access-deal-infos";
import { useDispatch, useSelector } from "react-redux";
import { GetBasket, ItemField, UpdateItemRootOnBasket } from "../../../Redux/Reducers/redux-slice-basket";
import { useTranslation } from "react-i18next";

enum ApplyToAllTypes {
  Price,
  Contract,
  Customer,
}

export type ModalCheckoutBillingOverrideElementProps = {
  onClose?: () => void;
  item: DbOrderBasketItemInfos;
  customers: DbCustomerInfos[];
  actions?: { attachment?: boolean; overridePrice?: boolean; overrideCustomer?: boolean };
};

export type ModalCheckoutBillingOverrideProps = CommonModalProps<ModalCheckoutBillingOverrideElementProps>;

const ModalCheckoutBillingOverride = React.forwardRef<any, ModalCheckoutBillingOverrideProps>((props, ref) => {
  const { t } = useTranslation(); // Initialize translation hook
  const { item, customers, actions, closeModal, onClose } = props;
  const dispatch = useDispatch();
  const [price, setPrice] = useState<number>(
    !item.overridenPrice ? item.totalPrice!.price! : item.overridenPrice.price!
  );

  const [currency, setCurrency] = useState<CurrencyTypes>(
    item.overridenPrice !== null && item.overridenPrice?.currency !== null
      ? item.overridenPrice!.currency
      : item.totalPrice!.currency!
  );

  const [customer, setCustomer] = useState<DbCustomerInfos | undefined>(
    customers?.find((x) => x.id === item.customerIdOverride)
  );
  const [customerId, setCustomerId] = useState<number | null>(
    item.customerIdOverride ? item.customerIdOverride : item.contractExternalCustomerId
  );
  const [contract, setContract] = useState<DbAccessDealInfos | undefined>(
    item.contractExternalDealId
      ? item.contractDeals?.find((x) => x.externalId === item.contractExternalDealId)
      : undefined
  );
  const [contractDealId, setContractDealId] = useState<string | null>(item.contractExternalDealId);
  const [contractCustomerId, setContractCustomerId] = useState<number | null>(item.contractExternalCustomerId);

  const basket = useSelector(GetBasket);

  const handleCancelClick: React.MouseEventHandler = () => {
    closeModal();
  };

  const handleApplyToAll = (type: ApplyToAllTypes) => {
    basket.items?.forEach((item) => {
      switch (type) {
        case ApplyToAllTypes.Price:
          dispatch(
            UpdateItemRootOnBasket({
              itemId: item.id,
              field: ItemField.PriceOverride,
              value: { price: price, currency: currency },
            })
          );
          break;
        case ApplyToAllTypes.Contract:
          // Check if contract is available on other items
          if (!item.contractDeals?.find((x) => x.externalId === contractDealId)) return;

          dispatch(
            UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.ContractDealOverride, value: contractDealId })
          );
          dispatch(
            UpdateItemRootOnBasket({
              itemId: item.id,
              field: ItemField.ContractCustomerOverride,
              value: contractCustomerId,
            })
          );

          break;
        case ApplyToAllTypes.Customer:
          dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.CustomerOverride, value: customerId }));

          break;
      }
    });
  };

  const handleConfirmClick: React.MouseEventHandler = () => {
    // Update redux
    dispatch(
      UpdateItemRootOnBasket({
        itemId: item.id,
        field: ItemField.PriceOverride,
        value: { price: price, currency: currency },
      })
    );
    dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.CustomerOverride, value: customerId }));
    dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.ContractDealOverride, value: contractDealId }));
    dispatch(
      UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.ContractCustomerOverride, value: contractCustomerId })
    );

    onClose && onClose();
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.billingOptions")}
      reference={ref}
      closeModal={closeModal}
      width={800}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button onClick={() => handleApplyToAll(ApplyToAllTypes.Price)}>{t("button:applyPriceToAllItems")}</Button>
          <Button onClick={() => handleApplyToAll(ApplyToAllTypes.Customer)}>
            {t("button:applyCustomerToAllItems")}
          </Button>
          <Button onClick={() => handleApplyToAll(ApplyToAllTypes.Contract)}>
            {t("button:applyContractToAllItems")}
          </Button>

          <Button variant="contained" onClick={handleConfirmClick}>
            {t("common:done")}
          </Button>
        </Stack>
      }
    >
      {actions?.overridePrice && (
        <Grid container spacing={3} mb={1.5}>
          <Grid item md={9}>
            <Stack>
              <InputLabel sx={{ mb: 0.5 }} htmlFor="form-id">
                {t("common:price")}
              </InputLabel>
              <OutlinedInput
                id="form-id"
                type="number"
                onChange={(e) => setPrice(parseFloat(e.target.value))}
                value={price}
                inputProps={{
                  maxLength: 13,
                  step: "0.1",
                }}
              />
            </Stack>
          </Grid>
          <Grid item md={3}>
            <Stack>
              <Select
                value={currency}
                inputProps={{ "aria-label": "Without label" }}
                id="form-currency"
                sx={{ mt: 3.1 }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                onChange={(e, v) => setCurrency(e.target.value as CurrencyTypes)}
              >
                <MenuItem value={CurrencyTypes.EUR}>EUR</MenuItem>
                <MenuItem value={CurrencyTypes.USD}>USD</MenuItem>
              </Select>
            </Stack>
          </Grid>
        </Grid>
      )}
      {actions?.overrideCustomer && (
        <Grid container spacing={3}>
          <Grid item md={6}>
            <Stack>
              <InputLabel sx={{ mb: 0 }} htmlFor="form-contract">
                {t("common:contract")}
              </InputLabel>
              <Autocomplete
                sx={{ mt: -2 }}
                id="form-contract"
                options={item.contractDeals === null ? [] : item.contractDeals}
                onChange={(e, v) => {
                  setContract(v === null ? undefined : v);
                  setContractDealId(v === null ? null : v.externalId);
                  setContractCustomerId(v === null ? null : v.customerId);
                }}
                getOptionLabel={(value: any) => "#" + value.externalId + " - " + value.customerName}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={contract}
                renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.selectContract")} />}
              />
            </Stack>
          </Grid>
          <Grid item md={6}>
            <Stack>
              <InputLabel sx={{ mb: 0 }} htmlFor="form-customer">
                {t("field:label.customerOverride")}
              </InputLabel>
              <Autocomplete
                sx={{ mt: -2 }}
                id="form-customer"
                options={customers}
                onChange={(e, v) => {
                  setCustomer(v === null ? undefined : v);
                  setCustomerId(v === null ? null : v.id);
                }}
                getOptionLabel={(value: any) => value.name + " #" + value.customerCodeId}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={customer}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t("field:placeholder.customerOverride")} />
                )}
              />
            </Stack>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
});

export default ModalCheckoutBillingOverride;
